import { fetchEntries, fetchOneEntry } from '@builder.io/sdk-react';

import { logger } from '~/src/common/services/Logger';
import { getPathname, getSearchParams } from '~/src/common/utils/url';

import { BuilderFetchOptions, DataModel, PageModel, SectionModel } from './types';

/**
 * Fonction générique de récupération d'un contenu Builder de type page ou section.
 * Attend en entrée le nom du modèle (unique au sein des pages/sections) ainsi que l'url de la page désirée.
 * Retourne un couple [queryKey, queryFn] utilisable directement avec RQ
 */
const getModelEntry = (model: PageModel | SectionModel, configOptions: BuilderFetchOptions) => {
  // La key utilisée pour le cache de react-query
  const queryKey = ['BuilderIO', 'model', model, configOptions.query];
  // La fonction de récupération des données
  const queryFn = async () => {
    const config = { ...configOptions, model };
    try {
      return await fetchOneEntry({ ...config, apiKey: process.env.NEXT_PUBLIC_BUILDER_API_KEY });
    } catch (originalError) {
      logger.error('Unable to load BuilderIO content', { originalError, ...config });
      return null;
    }
  };
  return [queryKey, queryFn] as const;
};

export const getPageModelEntry = (model: PageModel, path: string, opts?: BuilderFetchOptions) => {
  const urlPath = getPathname(path) ?? '';
  return getModelEntry(model, {
    options: { ...getSearchParams(path), ...opts },
    userAttributes: { urlPath },
  });
};

export const getSectionModelEntry = (
  section: SectionModel,
  id: string,
  opts?: BuilderFetchOptions,
) => {
  return getModelEntry(section, {
    options: opts,
    query: { id },
  });
};

/**
 * Récupere de multiples entrées depuis un modèle de données de BuilderIO
 */
export const getDataEntries = <T>(model: DataModel, opts?: BuilderFetchOptions) => {
  // La key utilisée pour le cache de react-query
  const queryKey = ['BuilderIO', 'model', model, opts];
  // La fonction de récupération des infos
  const queryFn = async () => {
    try {
      return await fetchEntries<T>({
        model,
        ...opts,
        apiKey: process.env.NEXT_PUBLIC_BUILDER_API_KEY,
      });
    } catch (originalError) {
      logger.error('Unable to load BuilderIO content', { originalError, model, ...opts });
      return null;
    }
  };

  return [queryKey, queryFn] as const;
};

/**
 * Récupere une entrée unique depuis un modèle de données de BuilderIO
 */
export const getDataEntry = <T>(model: DataModel, opts?: BuilderFetchOptions) => {
  // La key utilisée pour le cache de react-query
  const queryKey = ['BuilderIO', 'model', model, opts];
  // La fonction de récupération des infos
  const queryFn = async () => {
    try {
      return await fetchOneEntry<T>({
        model,
        ...opts,
        apiKey: process.env.NEXT_PUBLIC_BUILDER_API_KEY,
      });
    } catch (originalError) {
      logger.error('Unable to load BuilderIO content', { originalError, model, ...opts });
      return null;
    }
  };

  return [queryKey, queryFn] as const;
};
